import { useCallback, useEffect, useState } from 'react';
import { useRoute } from './lib/router.js';
import {
  getTelegramConfig,
  getTelegramMe,
  type TelegramConfig,
  type TelegramUser,
} from './lib/api.js';
import { setAvatar, setNickname } from './lib/storage.js';
import { AppHeader } from './components/AppHeader.js';
import { TelegramGate } from './components/TelegramGate.js';
import { HomePage } from './pages/HomePage.js';
import { NewRoomPage } from './pages/NewRoomPage.js';
import { OpenRoomsPage } from './pages/OpenRoomsPage.js';
import { ProfilePage } from './pages/ProfilePage.js';
import { RoomPage } from './pages/RoomPage.js';

export function App(): JSX.Element {
  const { route, go } = useRoute();
  const [telegram, setTelegram] = useState<TelegramConfig | null>(null);
  const [account, setAccount] = useState<TelegramUser | null>(null);
  /** Пока не знаем, нужен ли вход, не показываем ни дверь, ни страницу. */
  const [checked, setChecked] = useState(false);

  const accept = useCallback((user: TelegramUser): void => {
    setAccount(user);
    // Имя и аватарка из телеграма становятся своими сразу: иначе человек
    // войдёт и всё равно окажется в комнате «Гостем».
    if (user.displayName) setNickname(user.displayName);
    if (user.avatarUrl) {
      setAvatar((current) => ({ ...current, photoUrl: user.avatarUrl }));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [config, user] = await Promise.all([
          getTelegramConfig(),
          getTelegramMe().catch(() => null),
        ]);
        if (cancelled) return;
        setTelegram(config);
        if (user) accept(user);
      } catch {
        // Сервер не ответил про телеграм — значит его тут нет, и дверь
        // ставить не из-за чего.
        if (!cancelled) setTelegram({ enabled: false, botUsername: null, required: false });
      } finally {
        if (!cancelled) setChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accept]);

  if (!checked) return <main className="shell" aria-busy="true" />;

  if (telegram?.required && !account) {
    return <TelegramGate config={telegram} onLogin={accept} />;
  }

  if (route.name === 'room') {
    // key заставляет пересоздать страницу при переходе между комнатами:
    // соединение и состояние плеера не должны переезжать из старой комнаты.
    return <RoomPage key={route.code} code={route.code} onNavigate={go} />;
  }

  // Общая шапка есть у всех экранов, кроме комнаты: там своя, и место
  // на экране отдано видео.
  return (
    <>
      <AppHeader route={route} onNavigate={go} />
      {route.name === 'new' && <NewRoomPage onNavigate={go} />}
      {route.name === 'rooms' && <OpenRoomsPage onNavigate={go} />}
      {route.name === 'profile' && <ProfilePage onNavigate={go} />}
      {route.name === 'home' && <HomePage onNavigate={go} />}
    </>
  );
}
